import React, { ReactElement, useEffect, useState } from 'react'

import { PagedListRequest } from 'domain/types/global/pagedListRequest';

import { useTranslation } from 'react-i18next';
import { RootStateCampanha } from '../../../../store/campanhaReducer';
import moment from 'moment';
import 'moment/dist/locale/pt-br'

import { connectUtil, PropsFromRedux } from 'base/src/utils/reduxUtil';
import { LotePedido, ResetLotePedido } from '../../../../store/pedidoLotes/actions/pedidoLotesAction';
import { LotePedidoResponse } from '../../../../types/lotePedido/lotePedidoResponse';
import { LOTEPEDIDO_GET } from '../../../../types/lotePedido/lotePedido';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Chip,
  SelectChangeEvent,
  Badge
} from '@mui/material';
import { Icon } from '@iconify/react';
import { formatDate } from 'base/src/utils/stringUtil';
import { formatToBRL } from 'base/src/utils/mathUtil';
import { Tag } from 'base/src/components/Tag';
import { Button } from 'base/src/components/Button';
import NoData from 'base/src/components/NoData';
import { Modal, ModalContent } from 'base/src/components/Modal';
import { LoteActionsPopover } from './LoteActionsPopover';
import ModalRealizarPagamento from '../ModalRealizarPagamento/ModalRealizarPagamento';
import ModalDeletarPagamento from '../ModalDeletarPagamento/ModalDeletarPagamento';
import PagedLoteItemDetalhe from '../PagedLoteItemDetalhe/PagedLoteItemDetalhe';
import FilterDrawer from './components/filterDrawer/filterDrawer';
import { CampanhaPedidoResgateResponse } from '../../../../types/campanha/campanhaPedidoResgateResponse';
import { ObjectControl } from 'domain/utils/ReactUtils';
import ModalAdicionarSaldo from '../ModalAdicionarSaldo/ModalAdicionarSaldo';
import ModalNotaRepasse from '../ModalNotaRepasse';
import ModalNotaFiscal from '../ModalNotaFiscal';

const connector = connectUtil(
  (state: RootStateCampanha) => ({
    LotePedidoState: state.PedidoLotesReducer.LotePedido,
    CampanhasDisponiveis: state.CampanhasReducer?.campanhasAll,
    mudancaStatus: state.PedidoLotesReducer.mudancaStatus,
    loading: state.BaseReducer.Loading,
    CarteiraUsuarioInfo: state.CarteiraBaseReducer.CarteiraUsuario,
  }),
  { LotePedido, ResetLotePedido }
);

export interface PagedResgatesProps extends PropsFromRedux<typeof connector> {
  take: number;
  campanhaId: number;
  action?: React.ReactElement;
  navigatePagarPedidoButton?: ReactElement;
  openModalLote?: ReactElement;
}


function PagedAprovar(props: PagedResgatesProps) {
  const [request, setRequest] = useState<PagedListRequest<LotePedidoResponse>>({
    skip: 0,
    take: props.take,
    search: '',
    filter: [],
    data: {} as LotePedidoResponse
  });

  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [isAnyPopoverOpen, setIsAnyPopoverOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<LotePedidoResponse | null>(null);
  const [openModal, setOpenModal] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [sortField, setSortField] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');

  const modalRequest = {} as CampanhaPedidoResgateResponse;
  const modalControl = new ObjectControl(modalRequest, () => { });

  const { t } = useTranslation();

  moment.locale("pt-br");

  // Status options
  const statusOptions = [
    { id: '1', nome: 'Pendente' },
    { id: '2', nome: 'Processando' },
    { id: '3', nome: 'Pago' },
    { id: '4', nome: 'Excluido' },
    { id: '5', nome: 'Parcialmente Pago' }
  ];

  // Get chip color for status tags
  function getStatusChipColor(statusId: number): 'default' | 'warning' | 'success' | 'error' {
    if (statusId === 1) return 'warning';
    if (statusId === 2) return 'default';
    if (statusId === 3) return 'success';
    if (statusId === 4) return 'error';
    if (statusId === 5) return 'warning';
    return 'default';
  }

  // Handle status chip toggle
  function handleStatusChipToggle(statusId: string) {
    const newStatusFilter = statusFilter.includes(statusId)
      ? statusFilter.filter(id => id !== statusId)
      : [...statusFilter, statusId];

    setStatusFilter(newStatusFilter);

    const filterArray = newStatusFilter.length > 0 ? [{
      Values: newStatusFilter,
      Id: 0,
      Name: "Status"
    }] : [];

    const newRequest = {
      ...request,
      filter: filterArray,
      skip: 0
    };
    setRequest(newRequest);

    props.ResetLotePedido(newRequest, props.take);
    props.LotePedido([props.campanhaId], newRequest, t);
  }

  // Handle pagination change
  function handlePageChange(_: unknown, newPage: number) {
    const newRequest = {
      ...request,
      skip: newPage * request.take
    };
    setRequest(newRequest);

    // Reset data to clear previous page data
    props.ResetLotePedido(newRequest, props.take);

    // Load new page data
    props.LotePedido([props.campanhaId], newRequest, t);
  }

  // Handle rows per page change
  function handleRowsPerPageChange(event: React.ChangeEvent<HTMLInputElement>) {
    const newTake = parseInt(event.target.value, 10);
    const newRequest = {
      ...request,
      take: newTake,
      skip: 0
    };
    setRequest(newRequest);

    // Reset data to clear previous data
    props.ResetLotePedido(newRequest, newTake);

    // Load new data
    props.LotePedido([props.campanhaId], newRequest, t);
  }

  // Handle search
  function handleSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    const searchVal = event.target.value;
    setSearchValue(searchVal);

    const newRequest = {
      ...request,
      search: searchVal,
      skip: 0
    };
    setRequest(newRequest);

    // Reset data to clear previous results
    props.ResetLotePedido(newRequest, props.take);

    // Load new filtered data
    props.LotePedido([props.campanhaId], newRequest, t);
  }

  function handleStatusFilterChange(event: SelectChangeEvent<string[]>) {
    const values = event.target.value as string[];
    setStatusFilter(values);

    const filterArray = values.length > 0 ? [{
      Values: values,
      Id: 0,
      Name: "Status"
    }] : [];

    const newRequest = {
      ...request,
      filter: filterArray,
      skip: 0
    };
    setRequest(newRequest);

    props.ResetLotePedido(newRequest, props.take);

    props.LotePedido([props.campanhaId], newRequest, t);
  }

  function handleOpenDrawer() {
    setIsDrawerOpen(true);
  }

  function handleCloseDrawer() {
    setIsDrawerOpen(false);
  }

  // Handle apply filters (close drawer after applying)
  function handleApplyFilters() {
    setIsDrawerOpen(false);
  }

  function handleDateStartChange(value: string) {
    setDateStart(value);

    const newRequest = {
      ...request,
      data: {
        ...request.data,
        DataInicio: value ? `${value}T00:00:00` : undefined,
        DataFim: dateEnd ? `${dateEnd}T23:59:59` : undefined
      },
      skip: 0
    };
    setRequest(newRequest);

    props.ResetLotePedido(newRequest, props.take);
    props.LotePedido([props.campanhaId], newRequest, t);
  }

  function handleDateEndChange(value: string) {
    setDateEnd(value);

    const newRequest = {
      ...request,
      data: {
        ...request.data,
        DataInicio: dateStart ? `${dateStart}T00:00:00` : undefined,
        DataFim: value ? `${value}T23:59:59` : undefined
      },
      skip: 0
    };
    setRequest(newRequest);

    props.ResetLotePedido(newRequest, props.take);
    props.LotePedido([props.campanhaId], newRequest, t);
  }

  function handleClearFilters() {
    setSearchValue('');
    setStatusFilter([]);
    setDateStart('');
    setDateEnd('');

    // Reset request to initial state
    const newRequest = {
      ...request,
      search: '',
      filter: [],
      data: {} as LotePedidoResponse,
      skip: 0
    };
    setRequest(newRequest);

    // Reset data and load fresh data without filters
    props.ResetLotePedido(newRequest, props.take);
    props.LotePedido([props.campanhaId], newRequest, t);
  }

  // Format pagination label
  function formatPaginationLabel({ from, to, count }: { from: number; to: number; count: number }) {
    return `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`;
  }

  // Map column keys to backend sort field paths
  const sortFieldMap: Record<string, string> = {
    'lote': 'Nome',
    'campanha': 'CampanhaID',
    'data': 'RealizadoEm',
    'status': 'CampanhaPedidoResgatePagamentoStatusID',
    'pedidos': 'QtdeUsuarios',
    'valor': 'ValorTotal'
  };

  // Handle column sort (server-side)
  function handleSort(field: string) {
    const backendField = sortFieldMap[field];
    if (!backendField) return;

    const isAsc = sortField === field && sortOrder === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setSortOrder(newOrder);
    setSortField(field);

    const newRequest = {
      ...request,
      sort: `${backendField} ${newOrder}`,
      skip: 0
    };
    setRequest(newRequest);

    props.ResetLotePedido(newRequest, props.take);
    props.LotePedido([props.campanhaId], newRequest, t);
  }

  // Render sort icon
  function renderSortIcon(field: string) {
    if (sortField !== field) {
      return <Icon icon="mdi:unfold-more-horizontal" style={{ marginLeft: 4, fontSize: 16, opacity: 0.5 }} />;
    }
    return sortOrder === 'asc'
      ? <Icon icon="mdi:arrow-up" style={{ marginLeft: 4, fontSize: 16 }} />
      : <Icon icon="mdi:arrow-down" style={{ marginLeft: 4, fontSize: 16 }} />;
  }

  function handleModalClose() {
    // Fecha o modal
    setOpenModal(null);
    setSelectedItem(null);

    props.ResetLotePedido(request, props.take);
    props.LotePedido([props.campanhaId], request, t);

    window.dispatchEvent(new CustomEvent('reloadLoteList'));
  }

  function handlePopoverAction(action: string, item: LotePedidoResponse) {
    setSelectedItem(item);
    setOpenModal(action);
  }

  function handleModalOpenChange(open: boolean) {
    if (!open) {
      setOpenModal(null);
      setSelectedItem(null);

      // Refresh data after modal closes to ensure we have the latest status
      props.ResetLotePedido(request, props.take);
      props.LotePedido([props.campanhaId], request, t);
    }
  }

  function handleModalCancel() {
    handleModalOpenChange(false);
  }

  // Reset data when status changes and reload fresh data
  useEffect(() => {
    if (props.mudancaStatus && Object.keys(props.mudancaStatus).length > 0) {
      // Reset data first
      props.ResetLotePedido(request, props.take);

      // Then reload with fresh data
      props.LotePedido([props.campanhaId], request, t);
    }
  }, [props.mudancaStatus]);

  useEffect(() => {
    // Adiciona listener para o evento customizado de reload
    function handleReloadLoteList() {
      props.LotePedido([props.campanhaId], request, t);
    }

    window.addEventListener('reloadLoteList', handleReloadLoteList);

    // Cleanup do listener
    return () => {
      window.removeEventListener('reloadLoteList', handleReloadLoteList);
    };
  }, [request, props.take, props.campanhaId, t]);

  // Load initial data
  useEffect(() => {
    props.LotePedido([props.campanhaId], request, t);
  }, [props.campanhaId]);

  // Get data from state
  const data = props.LotePedidoState?.Result || [];
  const totalCount = props.LotePedidoState?.TotalCount || 0;
  const isLoading = props.loading.some(l => l.Name === LOTEPEDIDO_GET);

  const currentPage = Math.floor(request.skip / request.take);
  const activeFilterCount = statusFilter.length + (dateStart ? 1 : 0) + (dateEnd ? 1 : 0);

  return (
    <Box sx={{
      '& @keyframes fadeIn': {
        '0%': {
          opacity: 0,
          transform: 'translateY(10px)'
        },
        '100%': {
          opacity: 1,
          transform: 'translateY(0)'
        }
      }
    }}>
      {/* Action Bar: Botão Voltar - SearchBar - Filtros - Gerar Lote */}
      <Box sx={{
        mb: 3,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap'
      }}>
        {/* Left Side: Botão Voltar + Search Bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          {/* Botão Voltar */}
          {props.navigatePagarPedidoButton && (
            <Box sx={{
              '& button': {
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                '& .MuiButton-startIcon': {
                  marginRight: 0.5
                }
              }
            }}>
              {/* Clonamos o botão para adicionar o ícone */}
              {React.cloneElement(props.navigatePagarPedidoButton as React.ReactElement, {
                startIcon: <Icon icon="mdi:arrow-left" />
              })}
            </Box>
          )}

          {/* Search Bar */}
          <TextField
            label="Pesquisar lotes"
            placeholder="Digite o nome do lote"
            value={searchValue}
            onChange={handleSearchChange}
            variant="outlined"
            size="small"
            sx={{
              minWidth: 300,
              flex: 1,
              maxWidth: 400,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
              }
            }}
            InputProps={{
              startAdornment: (
                <Icon
                  icon="mdi:magnify"
                  style={{
                    marginRight: 8,
                    color: '#9CA3AF',
                    width: 20,
                    height: 20
                  }}
                />
              ),
            }}
          />
        </Box>

        {/* Right Side: Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Badge
            badgeContent={activeFilterCount}
            color="primary"
            invisible={activeFilterCount === 0}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.7rem',
                height: 20,
                minWidth: 20,
                borderRadius: '50%',
              }
            }}
          >
            <Button
              size="md"
              variant="secondary"
              iconName="mdi:filter-variant"
              onClick={handleOpenDrawer}
            >
              Filtros
            </Button>
          </Badge>

          {props.openModalLote && (
            <Box>
              {props.openModalLote}
            </Box>
          )}
        </Box>
      </Box>

      {/* Status Tags */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        {statusOptions.map(function renderStatusChip(status) {
          const isSelected = statusFilter.includes(status.id);
          const chipColor = getStatusChipColor(Number(status.id));

          return (
            <Chip
              key={status.id}
              label={status.nome}
              onClick={() => handleStatusChipToggle(status.id)}
              variant={isSelected ? 'filled' : 'outlined'}
              color={chipColor}
              size="small"
              sx={{
                fontWeight: isSelected ? 600 : 400,
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }
              }}
            />
          );
        })}
      </Box>

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Table>
          <TableHead sx={{ bgcolor: 'grey.50' }}>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: 'grey.700',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
                onClick={() => handleSort('lote')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Lote
                  {renderSortIcon('lote')}
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: 'grey.700',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
                onClick={() => handleSort('campanha')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Campanha
                  {renderSortIcon('campanha')}
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: 'grey.700',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
                onClick={() => handleSort('data')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Data
                  {renderSortIcon('data')}
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: 'grey.700',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
                onClick={() => handleSort('status')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Status
                  {renderSortIcon('status')}
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: 'grey.700',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
                onClick={() => handleSort('pedidos')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Pedidos
                  {renderSortIcon('pedidos')}
                </Box>
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 600,
                  color: 'grey.700',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
                onClick={() => handleSort('valor')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  Valor
                  {renderSortIcon('valor')}
                </Box>
              </TableCell>
              <TableCell width={50} sx={{ fontWeight: 600, color: 'grey.700' }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 8, border: 'none' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={40} thickness={4} />
                    <Typography variant="body2" color="text.secondary">
                      Carregando dados...
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, border: 'none' }}>
                  <NoData />
                </TableCell>
              </TableRow>
            ) : (
              data.map((item, index) => {
                return (
                  <TableRow
                    key={item.CampanhaPedidoResgatePagamentoID || index}
                    hover
                    sx={{
                      '&:hover': !isAnyPopoverOpen ? {
                        bgcolor: 'action.hover',
                        cursor: 'pointer',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                      } : {},
                      '&:nth-of-type(even)': {
                        bgcolor: 'grey.25'
                      },
                      transition: !isAnyPopoverOpen ? 'all 0.2s ease-in-out' : 'none',
                      animation: 'fadeIn 0.3s ease-in-out'
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} sx={{ color: 'grey.900' }}>
                        {item.Nome}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'grey.700' }}>
                        {item.CampanhaID}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'grey.700' }}>
                        {moment(item.RealizadoEm).format('DD/MM/YYYY HH:mm')}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {item.CampanhaPedidoResgatePagamentoStatusID && (
                        <Tag

                          variant={
                            item.CampanhaPedidoResgatePagamentoStatusID === 1
                              ? 'warning'
                              : item.CampanhaPedidoResgatePagamentoStatusID === 2
                                ? 'primary'
                                : item.CampanhaPedidoResgatePagamentoStatusID === 3
                                  ? 'positive'
                                  : item.CampanhaPedidoResgatePagamentoStatusID === 4
                                    ? 'danger'
                                    : item.CampanhaPedidoResgatePagamentoStatusID === 5
                                      ? 'neutral'
                                      : 'neutral'
                          }
                        >
                          {item.CampanhaPedidoResgatePagamentoStatusID === 1
                            ? 'Pendente'
                            : item.CampanhaPedidoResgatePagamentoStatusID === 2
                              ? 'Processando'
                              : item.CampanhaPedidoResgatePagamentoStatusID === 3
                                ? 'Pago'
                                : item.CampanhaPedidoResgatePagamentoStatusID === 4
                                  ? 'Excluido'
                                  : item.CampanhaPedidoResgatePagamentoStatusID === 5
                                    ? 'Parcialmente Pago'
                                    : item.CampanhaPedidoResgatePagamentoStatusID}
                        </Tag>
                      )}
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'grey.700' }}>
                        {item.QtdeUsuarios} pedidos
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'success.main',
                            fontWeight: 700,
                            fontSize: '0.95rem'
                          }}
                        >
                          {formatToBRL(item.ValorTotal)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'grey.600' }}>
                          Valor total
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <LoteActionsPopover
                          item={item}
                          campanhaId={props.campanhaId}
                          onAction={handlePopoverAction}
                          onPopoverOpenChange={setIsAnyPopoverOpen}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <Box sx={{ backgroundColor: 'grey.25', borderTop: '1px solid', borderColor: 'grey.200' }}>
          <TablePagination
            component="div"
            count={totalCount}
            page={currentPage}
            onPageChange={handlePageChange}
            rowsPerPage={request.take}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Itens por página:"
            labelDisplayedRows={formatPaginationLabel}
            sx={{
              '& .MuiTablePagination-toolbar': {
                paddingX: 3,
                paddingY: 2
              },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                color: 'grey.700',
                fontWeight: 500
              },
              '& .MuiTablePagination-actions button': {
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.50'
                }
              }
            }}
          />
        </Box>
      </TableContainer>

      {/* Modals */}
      {selectedItem && (
        <>
          {/* Modal Adicionar Saldo */}
          <Modal open={openModal === 'adicionar-saldo'} onOpenChange={handleModalOpenChange}>
            <ModalContent>
              <ModalAdicionarSaldo
                ValorTotal={selectedItem.ValorTotal}
                lote={selectedItem}
                carteiraId={props.CarteiraUsuarioInfo[0].CarteiraID || 0}
              />
            </ModalContent>
          </Modal>

          {/* Modal Detalhes Pagamento */}
          <Modal open={openModal === 'detalhes-pagamento'} onOpenChange={handleModalOpenChange}>
            <ModalContent>
              <PagedLoteItemDetalhe
                loteDetail={selectedItem}
                control={modalControl}
                take={5}
                loteId={selectedItem.CampanhaPedidoResgatePagamentoID}
                campanhaId={props.campanhaId}
              />
            </ModalContent>
          </Modal>

          {/* Modal Efetuar Pagamento */}
          <Modal open={openModal === 'efetuar-pagamento'} onOpenChange={handleModalOpenChange}>
            <ModalContent>
              <ModalRealizarPagamento
                loteItem={selectedItem}
                onCancel={handleModalCancel}
                onModalClose={handleModalClose}
                campanhaId={props.campanhaId}
              />
            </ModalContent>
          </Modal>

          {/* Modal Nota Repasse */}
          <Modal open={openModal === 'nota-repasse'} onOpenChange={handleModalOpenChange}>
            <ModalContent>
              <Box>
                <ModalNotaRepasse
                  campanhaPedidoResgatePagamentoId={selectedItem.CampanhaPedidoResgatePagamentoID}
                  campanhaId={props.campanhaId}
                />
              </Box>
            </ModalContent>
          </Modal>

          {/* Modal Nota Fiscal */}
          <Modal open={openModal === 'baixar-notas'} onOpenChange={handleModalOpenChange}>
            <ModalContent>
              <Box>
                <ModalNotaFiscal
                  campanhaPedidoResgatePagamentoId={selectedItem.CampanhaPedidoResgatePagamentoID}
                  campanhaId={props.campanhaId}
                />
              </Box>
            </ModalContent>
          </Modal>

          {/* Modal Excluir */}
          <Modal open={openModal === 'excluir'} onOpenChange={handleModalOpenChange}>
            <ModalContent>
              <ModalDeletarPagamento
                loteItem={selectedItem}
                onCancel={handleModalCancel}
                onConfirm={handleModalClose}
              />
            </ModalContent>
          </Modal>
        </>
      )}

      {/* Filter Drawer */}
      <FilterDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        statusFilter={statusFilter}
        onStatusFilterChange={handleStatusFilterChange}
        dateStart={dateStart}
        dateEnd={dateEnd}
        onDateStartChange={handleDateStartChange}
        onDateEndChange={handleDateEndChange}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        statusOptions={statusOptions}
      />
    </Box>
  );
}
export default connector(PagedAprovar);